import { memo, type Ref, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { SearchIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { useBreakpointsContext } from '@air/provider-media-query';
import { useShortIdContext } from '@air/provider-short-id';

import { TopBar } from '~/_components/TopBar';
import { PublicBoardFiltersButton } from '~/components/Filters/FiltersButton/PublicBoardFiltersButton';
import { PublicSearch } from '~/components/Search/PublicSearch';
import { PublicBoardHeaderActions } from '~/components/Shared/HeaderBar/PublicBoardHeaderActions';
import { SearchRow } from '~/components/Shared/HeaderBar/SearchRow';
import { useMobileSearchBar } from '~/hooks/search/useMobileSearchBar';
import { usePublicHeaderHorizontalPadding } from '~/hooks/usePublicHeaderHorizontalPadding';
import { usePublicBoard } from '~/providers/PublicBoardProvider';
import { centralizedBoardSelector } from '~/store/centralizedBoard/selectors';

// Stacked above the workspace shell so the mobile search overlay can cover it.
const HEADER_Z_INDEX = 5;
const HEADER_Z_INDEX_MOBILE_SEARCH_OPEN = 6;

export type HeaderBarProps = {
  headerRef?: Ref<HTMLDivElement>;
};

export const PublicHeaderBar = memo(({ headerRef }: HeaderBarProps) => {
  const { isAboveMediumScreen } = useBreakpointsContext();
  const { openMobileSearch, isMobileSearchOpen, closeMobileSearch } = useMobileSearchBar();
  const { responsiveHorizontalPadding } = usePublicHeaderHorizontalPadding();
  const board = useSelector(centralizedBoardSelector);
  // The legacy board tree's contract mount sits above this header (see LegacyPublicBoardBindings);
  // the search bar takes the drill as a prop because it also renders on the v2 board page.
  const { onNavigateToBoard } = usePublicBoard();
  const { presentationSettings } = useShortIdContext();
  const hideSearchBar = useMemo(
    () => presentationSettings?.hideSearchBar === true,
    [presentationSettings?.hideSearchBar],
  );

  return (
    <TopBar
      className="ads:bg-base bg-gray-1 pb-0 md:pb-0"
      ref={headerRef}
      style={{
        paddingLeft: responsiveHorizontalPadding,
        paddingRight: responsiveHorizontalPadding,
        zIndex: isMobileSearchOpen ? HEADER_Z_INDEX_MOBILE_SEARCH_OPEN : HEADER_Z_INDEX,
      }}
    >
      <div className="flex w-full justify-between gap-6 pb-2 md:pb-3">
        <SearchRow>
          <div className="flex grow justify-end md:justify-start">
            {!hideSearchBar &&
              (isAboveMediumScreen || isMobileSearchOpen ? (
                <PublicSearch onCloseClick={closeMobileSearch} onNavigateToBoard={onNavigateToBoard} />
              ) : (
                <IconButton
                  adsIcon
                  appearance="ghost"
                  color="gray"
                  size="large"
                  icon={SearchIcon}
                  onClick={openMobileSearch}
                  label="Search"
                  data-testid="PUBLIC_HEADER_BAR_MOBILE_SEARCH_TRIGGER"
                />
              ))}
            {!isAboveMediumScreen && !hideSearchBar && (
              <div className="ml-2 flex items-center">
                <PublicBoardFiltersButton boardId={board?.id ?? ''} />
              </div>
            )}
          </div>
        </SearchRow>
        {!!board && <PublicBoardHeaderActions board={board} />}
      </div>
    </TopBar>
  );
});

PublicHeaderBar.displayName = 'PublicHeaderBar';
